import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';

const socket = io("https://webrtc.d-rosa.ai", {
  path: "/socket.io/",
  transports: ["websocket"],
  secure: true
});

const BT_SERVICE_UUID = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const BT_CHAR_TX_UUID = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";

async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ name: "ROSA_RELAY" }],
    optionalServices: [BT_SERVICE_UUID],
  });
  const server  = await device.gatt.connect();
  const service = await server.getPrimaryService(BT_SERVICE_UUID);
  const char    = await service.getCharacteristic(BT_CHAR_TX_UUID);
  return { device, char };
}

async function sendBTCommand(char, command) {
  const encoder = new TextEncoder();
  await char.writeValue(encoder.encode(command));
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function SafetyOutline() {
  const navigate = useNavigate();
  const localVideoRef  = useRef(null);
  const remoteVideoRef = useRef(null);
  const canvasRef      = useRef(null);
  const pcRef          = useRef(null);
  const localStreamRef = useRef(null);
  const intervalRef    = useRef(null);
  const relayStatesRef = useRef([]);
  const screenTrackRef = useRef(null);
  const btCharRef      = useRef(null);

  const [room, setRoom]       = useState("");
  const [user]                = useState("user-" + Math.floor(Math.random() * 10000));
  const [initiator, setInitiator] = useState(false);
  const [joined, setJoined]   = useState(false);
  const [relayStates, setRelayStates] = useState(Array(8).fill(false));
  const [commandLog, setCommandLog]   = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);

  const [btConnected, setBtConnected]   = useState(false);
  const [btConnecting, setBtConnecting] = useState(false);
  const [btDevice, setBtDevice]         = useState(null);

  const [relayLabels, setRelayLabels] = useState([
    "Relay 1","Relay 2","Relay 3","Relay 4",
    "Relay 5","Relay 6","Relay 7","Relay 8"
  ]);
  const [editIndex, setEditIndex] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [popup, setPopup] = useState({ show: false, message: "", success: true });

  const userEmail = sessionStorage.getItem("userEmail");
  const isMobile  = useIsMobile();

  const log = (msg) =>
    setCommandLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);

  const showPopup = (message, success = true) => {
    setPopup({ show: true, message, success });
    setTimeout(() => setPopup({ show: false, message: "", success: true }), 3000);
  };

  // -- Bluetooth --------------------------------------------------------------
  const handleBTConnect = async () => {
    if (!navigator.bluetooth) { log("Web Bluetooth not supported"); return; }
    try {
      setBtConnecting(true);
      log("Searching for ROSA_RELAY�");
      const { device, char } = await connectBluetooth();
      btCharRef.current = char;
      setBtDevice(device);
      setBtConnected(true);
      log(`Bluetooth connected to ${device.name}`);
      device.addEventListener("gattserverdisconnected", () => {
        btCharRef.current = null;
        setBtConnected(false);
        log("Bluetooth disconnected");
      });
    } catch (err) {
      log(`BT Error: ${err.message}`);
    } finally {
      setBtConnecting(false);
    }
  };

  const handleBTDisconnect = () => {
    btDevice?.gatt?.disconnect();
    btCharRef.current = null;
    setBtConnected(false);
    setBtDevice(null);
    log("Bluetooth disconnected by user");
  };

  // -- Keep relay ref in sync -------------------------------------------------
  useEffect(() => {
    relayStatesRef.current = relayStates;
  }, [relayStates]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.onloadedmetadata = () => {
        console.log("Remote video ready",
          remoteVideoRef.current.videoWidth,
          remoteVideoRef.current.videoHeight);
      };
    }
  }, []);

  // -- TURN fetch -------------------------------------------------------------
  const initializePeer = async () => {
    try {
      const response = await fetch("https://webrtc.d-rosa.ai/turn");
      const config   = await response.json();
      const pc = new RTCPeerConnection({ iceServers: config.iceServers });
      pcRef.current = pc;
      return pc;
    } catch (err) {
      console.log("TURN fetch failed", err);
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
      });
      pcRef.current = pc;
      return pc;
    }
  };

  // -- Limit image capture & upload -------------------------------------------
  const captureAndUploadLimitImage = async (limitNo) => {
    try {
      const video = remoteVideoRef.current;
      if (!video || !video.srcObject) {
        showPopup("Remote video not available!", false);
        return;
      }
      if (video.readyState < 2) {
        showPopup("Remote video not ready yet!", false);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width  = video.videoWidth  || 320;
      canvas.height = video.videoHeight || 240;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      const formData = new FormData();
      formData.append("limit_no", limitNo);
      formData.append("image", blob, `limit${limitNo}.png`);

      const response = await fetch("https://www.d-rosa.ai/save_limit_image", {
        method: "POST",
        body:   formData,
      });

      const text = await response.text();
      console.log("Server Response:", text);

      try {
        const result = JSON.parse(text);
        if (result.status === "success" || result.message) {
          showPopup(`Limit ${limitNo} saved successfully`, true);
          log(`Limit ${limitNo}: Saved successfully`);
        } else {
          showPopup(`Limit ${limitNo}: ${result.message || "Unknown error"}`, false);
          log(`Limit ${limitNo}: ${result.message}`);
        }
      } catch {
        if (response.ok) {
          showPopup(`Limit ${limitNo} saved successfully`, true);
          log(`Limit ${limitNo}: Saved successfully`);
        } else {
          showPopup(`Limit ${limitNo}: Server error`, false);
          log(`Limit ${limitNo}: Server returned non-JSON`);
        }
      }

    } catch (error) {
      console.error("Upload failed:", error);
      showPopup(`Limit ${limitNo}: Upload failed`, false);
      log(`Limit ${limitNo}: Upload failed � ${error.message}`);
    }
  };

  // -- Cleanup ----------------------------------------------------------------
  const cleanupPeer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (pcRef.current) pcRef.current.close();
    pcRef.current = null;
    if (localStreamRef.current)
      localStreamRef.current.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    screenTrackRef.current = null;
    setInitiator(false);
    setJoined(false);
    setRelayStates(Array(8).fill(false));
    setAudioEnabled(true);
    setVideoEnabled(true);
  };

  const updateRelayState = (relayNumber, state) => {
    setRelayStates(prev => {
      const updated = [...prev];
      updated[relayNumber - 1] = state;
      return updated;
    });
  };

  // -- DataChannel � binary streaming logs REMOVED ----------------------------
  const setupDataChannel = (channel) => {
    channel.onopen = () => {
      console.log("DataChannel opened");
      log("DataChannel connected");
    };

    channel.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "relay_binary") {
          const relayNum = data.relay;
          const action   = data.action;
          const command  = `${action.toUpperCase()}${relayNum}`;

          if (btCharRef.current) {
            try {
              await sendBTCommand(btCharRef.current, command);
              log(`BT (from peer) ${command}`);
            } catch (err) {
              log(`BT (from peer) failed: ${err.message}`);
            }
          } else {
            log(`Peer ? Relay ${relayNum} ${action.toUpperCase()} (BT not connected)`);
          }

          const newState = action === "on";
          setRelayStates(prev => {
            const updated = [...prev];
            updated[relayNum - 1] = newState;
            return updated;
          });

          if (pcRef.current?.dataChannel?.readyState === "open") {
            pcRef.current.dataChannel.send(
              JSON.stringify({ type: "relay_status", relay: relayNum, status: action })
            );
          }
        }

        if (data.type === "relay_status") {
          updateRelayState(data.relay, data.status === "on");
          log(`Relay ${data.relay} ? ${data.status.toUpperCase()} (sync)`);
        }

        // relay_stream messages intentionally ignored � no log

      } catch (err) {
        console.error("Invalid message", err);
      }
    };

    channel.onerror = (e) => console.error("DataChannel error:", e);
    channel.onclose = () => {
      console.log("DataChannel closed");
      log("DataChannel closed");
      clearInterval(intervalRef.current);
    };

    pcRef.current.dataChannel = channel;
  };

  // -- Relay trigger � BT only, no IP window.open ----------------------------
  const triggerRelay = async (relayNumber, action = relayStates[relayNumber - 1] ? 'off' : 'on') => {
    const command = `${action.toUpperCase()}${relayNumber}`;

    if (btCharRef.current) {
      try {
        await sendBTCommand(btCharRef.current, command);
        log(`BT ${command}`);
      } catch (err) {
        log(`BT send failed: ${err.message}`);
      }
    } else {
      log(`BT not connected � ${command} not sent`);
    }

    const newRelayStates = [...relayStates];
    newRelayStates[relayNumber - 1] = action === 'on';
    setRelayStates(newRelayStates);

    if (pcRef.current?.dataChannel?.readyState === 'open') {
      pcRef.current.dataChannel.send(JSON.stringify({
        type: 'relay_binary',
        relay: relayNumber,
        action,
      }));
      log(`DC ? Relay ${relayNumber} ${action.toUpperCase()} sent to peer`);
    }
  };

  // -- WebRTC -----------------------------------------------------------------
  const startCall = async () => {
    if (!pcRef.current) return;
    try {
      const dataChannel = pcRef.current.createDataChannel("relayChannel");
      setupDataChannel(dataChannel);
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      socket.emit("signal", { type: "offer", offer, room, user });
    } catch (err) { console.error("Offer error", err); }
  };

  const handleJoin = () => {
    if (room.trim()) {
      cleanupPeer();
      socket.emit("join", { room, user });
      setJoined(true);
    }
  };

  const handleEndCall = () => { cleanupPeer(); log("Call ended"); };

  const handleToggleAudio = () => {
    const track = localStreamRef.current?.getAudioTracks()?.[0];
    if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
  };

  const handleToggleVideo = () => {
    const track = localStreamRef.current?.getVideoTracks()?.[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  };

  const handleShareScreen = async () => {
    if (!pcRef.current || !localStreamRef.current) return;
    if (!screenSharing) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const screenTrack  = screenStream.getVideoTracks()[0];
        screenTrackRef.current = screenTrack;
        const sender = pcRef.current.getSenders().find(s => s.track && s.track.kind === "video");
        if (sender) sender.replaceTrack(screenTrack);
        screenTrack.onended = () => handleStopShare();
        setScreenSharing(true);
      } catch (err) { log(`Share Screen failed: ${err.message}`); }
    } else { handleStopShare(); }
  };

  const handleStopShare = () => {
    try {
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      const sender = pcRef.current?.getSenders().find(s => s.track && s.track.kind === "video");
      if (sender && videoTrack) sender.replaceTrack(videoTrack);
      screenTrackRef.current?.stop();
      screenTrackRef.current = null;
    } catch (err) { console.error("Stop share error:", err); }
    setScreenSharing(false);
  };

  // -- WebRTC effect ----------------------------------------------------------
  useEffect(() => {
    if (!joined) return;

    const setupConnection = async () => {
      const pc = await initializePeer();

      navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(stream => {
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      });

      pc.ontrack = event => {
        const [stream] = event.streams;
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = stream;
      };

      pc.onicecandidate = event => {
        if (event.candidate)
          socket.emit("signal", { type: "ice", candidate: event.candidate, room, user });
      };

      pc.ondatachannel = event => setupDataChannel(event.channel);

      socket.on("signal", async data => {
        if (!pcRef.current) return;
        if (data.type === "offer") {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await pcRef.current.createAnswer();
          await pcRef.current.setLocalDescription(answer);
          socket.emit("signal", { type: "answer", answer, room, user });
        } else if (data.type === "answer") {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
        } else if (data.type === "ice") {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      });

      socket.on("initiator", data => {
        if (data.initiatorUser === user) setInitiator(true);
      });
    };

    setupConnection();

    return () => {
      cleanupPeer();
      socket.off("signal");
      socket.off("initiator");
    };
  }, [joined]);

  useEffect(() => {
    if (joined && initiator) setTimeout(() => startCall(), 1500);
  }, [initiator]);

  // -- Relay + Limit button group ---------------------------------------------
  const relayWithLimit = (index) => (
    <div
      key={index}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: isMobile ? "4px" : "8px" }}
    >
      <button
        onClick={() => triggerRelay(index)}
        onDoubleClick={() => { setEditIndex(index); setEditValue(relayLabels[index - 1]); }}
        style={{
          width:        isMobile ? "70px" : "90px",
          height:       isMobile ? "58px" : "75px",
          borderRadius: "18px",
          border:       "none",
          background:   relayStates[index - 1] ? "#00c853" : "#ff4081",
          color:        "white",
          fontWeight:   "bold",
          fontSize:     isMobile ? "12px" : "14px",
          cursor:       "pointer",
          boxShadow:    "0 4px 10px rgba(0,0,0,.25)",
          transition:   "all .3s",
        }}
      >
        {editIndex === index ? (
          <input
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={() => {
              const updated = [...relayLabels];
              updated[index - 1] = editValue;
              setRelayLabels(updated);
              setEditIndex(null);
            }}
            style={{ width: "60px", fontSize: "11px", textAlign: "center" }}
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <>{relayLabels[index - 1]}<br />{relayStates[index - 1] ? "ON" : "OFF"}</>
        )}
      </button>

      <button
        onClick={() => captureAndUploadLimitImage(index)}
        style={{
          marginTop:    "5px",
          width:        isMobile ? "70px" : "90px",
          height:       "26px",
          borderRadius: "10px",
          border:       "none",
          background:   "#424242",
          color:        "white",
          fontWeight:   "bold",
          fontSize:     "10px",
          cursor:       "pointer",
          boxShadow:    "0 2px 6px rgba(0,0,0,.2)",
          transition:   "all .3s",
        }}
      >
        {`Limit ${index}`}
      </button>
    </div>
  );

  // -- Bluetooth bar ----------------------------------------------------------
  const BTBar = () => (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      gap: "12px", margin: "16px 0", flexWrap: "wrap",
    }}>
      <div style={{
        padding: "8px 20px", borderRadius: "20px",
        background: btConnected ? "#e8f5e9" : "#fce4ec",
        border: `2px solid ${btConnected ? "#00c853" : "#ff4081"}`,
        fontWeight: "bold", fontSize: "14px",
        color: btConnected ? "#1b5e20" : "#880e4f",
      }}>
        {btConnected ? `BT: ${btDevice?.name ?? "Connected"}` : "BT: Disconnected"}
      </div>
      {!btConnected ? (
        <button onClick={handleBTConnect} disabled={btConnecting}
          style={{
            padding: "8px 22px", borderRadius: "20px", border: "none",
            background: btConnecting ? "#90caf9" : "#1565c0",
            color: "white", fontWeight: "bold",
            cursor: btConnecting ? "default" : "pointer",
          }}
        >
          {btConnecting ? "Connecting�" : "Connect Bluetooth"}
        </button>
      ) : (
        <button onClick={handleBTDisconnect}
          style={{
            padding: "8px 22px", borderRadius: "20px", border: "none",
            background: "#d32f2f", color: "white", fontWeight: "bold", cursor: "pointer",
          }}
        >
          Disconnect BT
        </button>
      )}
    </div>
  );

  // -- Popup ------------------------------------------------------------------
  const Popup = () => popup.show ? (
    <div style={{
      position:     "fixed",
      top:          "30px",
      left:         "50%",
      transform:    "translateX(-50%)",
      zIndex:       9999,
      background:   popup.success ? "#00c853" : "#d32f2f",
      color:        "white",
      padding:      "14px 28px",
      borderRadius: "20px",
      fontWeight:   "bold",
      fontSize:     "15px",
      boxShadow:    "0 6px 20px rgba(0,0,0,0.3)",
      textAlign:    "center",
      minWidth:     "220px",
    }}>
      {popup.success ? "? " : "? "}{popup.message}
    </div>
  ) : null;

  const ctrlBtn = (bg) => ({
    padding: "9px 10px", borderRadius: "15px", border: "none",
    background: bg, color: "white", fontSize: "12px",
    fontWeight: "bold", cursor: "pointer", flex: "1 1 0",
  });

const crosshairStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40px",
  height: "40px",
  pointerEvents: "none",
  zIndex: 100,
};

const topLine = {
  position: "absolute",
  width: "2px",
  height: "12px",
  background: "red",
  left: "19px",
  top: "0px",
};

const bottomLine = {
  position: "absolute",
  width: "2px",
  height: "12px",
  background: "red",
  left: "19px",
  bottom: "0px",
};

const leftLine = {
  position: "absolute",
  width: "12px",
  height: "2px",
  background: "red",
  left: "0px",
  top: "19px",
};

const rightLine = {
  position: "absolute",
  width: "12px",
  height: "2px",
  background: "red",
  right: "0px",
  top: "19px",
};

  // ==========================================================================
  // JOIN SCREEN
  // ==========================================================================
  if (!joined) {
    return (
      <div style={{ minHeight: "100vh", padding: "20px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
        <Popup />
        <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "38px", marginBottom: "5px" }}>ROSA A</h1>
        <h2 style={{ textAlign: "center", color: "#6a1b9a", fontSize: "20px", marginBottom: "5px" }}>Safety Outline</h2>
        <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "20px", fontSize: "16px" }}>
          {userEmail && <div>Signed in as: {userEmail}</div>}
          User : {user}
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
          <div style={{
            background: "white", padding: "40px", borderRadius: "30px",
            width: "350px", textAlign: "center", boxShadow: "0 8px 30px rgba(0,0,0,.2)",
          }}>
            <h2>Join Room</h2>
            <input
              placeholder="Enter Room"
              value={room}
              onChange={e => setRoom(e.target.value)}
              style={{ width: "90%", padding: "15px", borderRadius: "15px", border: "1px solid #ddd", fontSize: "16px" }}
            />
            <br />
            <button onClick={handleJoin}
              style={{
                marginTop: "25px", padding: "12px 40px", border: "none",
                borderRadius: "20px", background: "#6a1b9a",
                color: "white", fontSize: "18px", cursor: "pointer",
              }}
            >
              Join
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // MOBILE LAYOUT
  // ==========================================================================
  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", padding: "12px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
        <Popup />
        <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "24px", margin: "0 0 2px" }}>ROSA A</h1>
        <h2 style={{ textAlign: "center", color: "#6a1b9a", fontSize: "13px", margin: "0 0 4px" }}>Safety Outline</h2>
        <div style={{ textAlign: "center", fontSize: "12px", marginBottom: "4px" }}>User : {user}</div>

        <BTBar />

        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "10px" }}>
          <div style={{ background: "white", padding: "6px", borderRadius: "16px", flex: 1 }}>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", marginBottom: "4px" }}>Local</div>
            <video ref={localVideoRef} autoPlay muted playsInline
              style={{ width: "100%", height: "200px", borderRadius: "12px", border: "3px solid green", objectFit: "cover", display: "block" }} />
          </div>
          <div style={{ background: "white", padding: "6px", borderRadius: "16px", flex: 1, position: "relative" }}>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", marginBottom: "4px" }}>Remote</div>
            <video ref={remoteVideoRef} autoPlay playsInline
              style={{ width: "100%", height: "200px", borderRadius: "12px", border: "3px solid blue", objectFit: "cover", display: "block" }} />
           <div style={crosshairStyle}>
  <div style={topLine}></div>
  <div style={bottomLine}></div>
  <div style={leftLine}></div>
  <div style={rightLine}></div>
</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "6px", justifyContent: "center", marginBottom: "12px" }}>
          <button onClick={handleToggleAudio} style={ctrlBtn("#1565c0")}>{audioEnabled  ? "Mute"       : "Unmute"   }</button>
          <button onClick={handleToggleVideo} style={ctrlBtn("#1565c0")}>{videoEnabled  ? "Camera Off" : "Camera On"}</button>
          <button onClick={handleShareScreen} style={ctrlBtn("#1565c0")}>{screenSharing ? "Stop Share" : "Share"    }</button>
          <button onClick={handleEndCall}     style={ctrlBtn("#d32f2f")}>End Call</button>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: "4px" }}>
          {[1,2,3,4].map(r => relayWithLimit(r))}
        </div>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
          {[5,6,7,8].map(r => relayWithLimit(r))}
        </div>

        <div style={{
          background: "white", padding: "14px", borderRadius: "16px",
          height: "150px", overflow: "auto", boxShadow: "0 4px 15px rgba(0,0,0,.15)",
        }}>
          <h3 style={{ margin: "0 0 6px", fontSize: "14px" }}>Command Log</h3>
          {commandLog.map((entry, i) =>
            <div key={i} style={{ fontSize: "11px", fontFamily: "monospace" }}>{entry}</div>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
    );
  }

  // ==========================================================================
  // DESKTOP LAYOUT
  // ==========================================================================
  return (
    <div style={{ minHeight: "100vh", padding: "20px", background: "linear-gradient(135deg,#ffecd2,#fcb69f)" }}>
      <Popup />
      <h1 style={{ textAlign: "center", color: "#c2185b", fontSize: "38px", marginBottom: "2px" }}>ROSA A</h1>
      <h2 style={{ textAlign: "center", color: "#6a1b9a", fontSize: "20px", marginBottom: "5px" }}>Safety Outline</h2>
      <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "10px", fontSize: "16px" }}>
        {userEmail && <div>Signed in as: {userEmail}</div>}
        User : {user}
      </div>

      <BTBar />

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {relayWithLimit(1)}{relayWithLimit(2)}
        </div>

        <div style={{ background: "white", padding: "12px", borderRadius: "25px", boxShadow: "0 6px 20px rgba(0,0,0,.2)" }}>
          <div style={{ textAlign: "center", fontWeight: "bold", marginBottom: "8px" }}>Local</div>
          <video ref={localVideoRef} autoPlay muted playsInline
            style={{ width: "320px", height: "240px", borderRadius: "20px", border: "4px solid green", objectFit: "cover" }} />
        </div>

   <div
  style={{
    background: "white",
    padding: "12px",
    borderRadius: "25px",
    boxShadow: "0 6px 20px rgba(0,0,0,.2)",
  }}
>
  <div
    style={{
      textAlign: "center",
      fontWeight: "bold",
      marginBottom: "8px",
    }}
  >
    Remote
  </div>

  <div
    style={{
      position: "relative",
      width: "320px",
      height: "240px",
    }}
  >
    <video
      ref={remoteVideoRef}
      autoPlay
      playsInline
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "20px",
        border: "4px solid blue",
        objectFit: "cover",
        display: "block",
      }}
    />

    <div style={crosshairStyle}>
      <div style={topLine}></div>
      <div style={bottomLine}></div>
      <div style={leftLine}></div>
      <div style={rightLine}></div>
    </div>
  </div>
</div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {relayWithLimit(3)}{relayWithLimit(4)}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "30px", flexWrap: "wrap" }}>
        <button onClick={handleToggleAudio}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {audioEnabled ? "Mute" : "Unmute"}
        </button>
        <button onClick={handleToggleVideo}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {videoEnabled ? "Camera Off" : "Camera On"}
        </button>
        <button onClick={handleShareScreen}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#1565c0", color: "white" }}>
          {screenSharing ? "Stop Share" : "Share Screen"}
        </button>
        <button onClick={handleEndCall}
          style={{ padding: "12px", borderRadius: "15px", border: "none", background: "#d32f2f", color: "white" }}>
          End Call
        </button>
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "30px", gap: "10px", flexWrap: "wrap" }}>
        {[5,6,7,8].map(r => relayWithLimit(r))}
      </div>

      <div style={{
        marginTop: "30px", background: "white", padding: "20px",
        borderRadius: "20px", height: "180px", overflow: "auto",
        boxShadow: "0 4px 15px rgba(0,0,0,.15)",
      }}>
        <h3>Command Log</h3>
        {commandLog.map((entry, i) =>
          <div key={i} style={{ fontSize: "13px", fontFamily: "monospace" }}>{entry}</div>
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}

export default SafetyOutline;